import React from "react";
import { Link } from "react-router-dom";
import { Mail, Award, Clock, Target, Users, CheckCircle } from "lucide-react";
import SEO from "../components/SEO";
import { getProfessionalServiceSchema, getBreadcrumbSchema } from "../utils/schema";
import "./About.css";

export const About = () => {
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'About', url: '/about' }
  ]);

  const professionalSchema = getProfessionalServiceSchema();

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [breadcrumbSchema, professionalSchema]
  };

  const qualifications = [
    {
      title: "FCCA Qualified",
      description:
        "Fellow Chartered Certified Accountant with the Association of Chartered Certified Accountants",
      icon: <Award size={32} />,
    },
    {
      title: "MAAT Member",
      description:
        "Member of the Association of Accounting Technicians, demonstrating technical excellence",
      icon: <Award size={32} />,
    },
  ];

  const values = [
    {
      icon: <Target size={28} />,
      title: "Accuracy",
      description:
        "Meticulous attention to detail ensuring error-free accounts and tax returns",
    },
    {
      icon: <Clock size={28} />,
      title: "Reliability",
      description:
        "Consistent delivery of services on time, every time, with no exceptions",
    },
    {
      icon: <Users size={28} />,
      title: "Partnership",
      description:
        "Building long-term relationships focused on your financial success",
    },
    {
      icon: <CheckCircle size={28} />,
      title: "Transparency",
      description:
        "Clear communication and honest advice with no hidden fees or surprises",
    },
  ];

  return (
    <div className="about-page">
      <SEO
        title="About Brightson Accounting | FCCA Qualified Accountant in Wolverhampton"
        description="Meet Sonali Mitra, FCCA & MAAT qualified accountant with 15+ years UK practice experience. Professional accounting services for Wolverhampton businesses and individuals."
        canonical="/about"
        keywords="FCCA accountant Wolverhampton, qualified accountant Wolverhampton, Sonali Mitra accountant, professional accountant West Midlands"
        schema={combinedSchema}
      />
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <h1>About Brightson Accounting</h1>
            <p>
              Professional accounting expertise serving Wolverhampton businesses
              and individuals for over 15 years
            </p>
          </div>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="section about-story">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2>Your Trusted Financial Partner in Wolverhampton</h2>
              <p>
                Brightson Accounting Limited is a professional accounting
                practice based in Wolverhampton, dedicated to providing expert
                financial services to small businesses, limited companies,
                landlords, freelancers, and contractors across the West
                Midlands.
              </p>
              <p>
                Led by a fully qualified FCCA and MAAT accountant with over 15
                years of UK practice experience, we combine technical expertise
                with a deep understanding of the local business landscape. Our
                mission is simple: to deliver accurate, compliant, and
                value-driven accounting services that help our clients achieve
                their financial goals.
              </p>
              <p>
                Whether you're navigating complex HMRC requirements, planning
                tax-efficient strategies, or seeking reliable bookkeeping
                support, we provide the professional guidance and personal
                attention your finances deserve.
              </p>
            </div>

            <div className="story-highlights">
              <div className="highlight-card">
                <div className="highlight-number">15+</div>
                <div className="highlight-text">Years UK Practice</div>
              </div>
              <div className="highlight-card">
                <div className="highlight-number">FCCA</div>
                <div className="highlight-text">& MAAT Qualified</div>
              </div>
              <div className="highlight-card">
                <div className="highlight-number">100%</div>
                <div className="highlight-text">HMRC Compliant</div>
              </div>
              <div className="highlight-card">
                <div className="highlight-number">6</div>
                <div className="highlight-text">
                  Specialist Services
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Sonali Section */}
      <section className="section section-alt meet-sonali-section">
        <div className="container">
          <div className="sonali-content">
            <div className="sonali-main">
              <h2>Meet Sonali Mitra, FCCA</h2>
              <p className="sonali-intro">
                Sonali Mitra is the chartered accountant who keeps your business
                sharp, compliant, and tax-smart — without the jargon or the
                drama.
              </p>
              <p>
                With 15+ years supporting Midlands SMEs, she cuts through the
                noise, handles the numbers you don't want to, and helps you make
                better financial calls all year round.
              </p>
              <p>
                If you want a chartered accountant who's quick to respond, easy
                to talk to, and genuinely invested in your growth — not just your
                year-end — Sonali's your go-to.
              </p>
              
              <div className="education-section">
                <h4>Education</h4>
                <p>
                  <strong>BSc Applied Accounting</strong> from Oxford Brookes University
                </p>
              </div>
              
              <div className="featured-section">
                <h4>Featured In</h4>
                <ul className="featured-list">
                  <li>
                    <a 
                      href="https://www.coventrytelegraph.net/news/local-news/sonalis-amazing-two-years-study-3097019" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="featured-link"
                    >
                      Coventry Telegraph
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://www.cilex.org.uk/media/media_releases/media_release_archive/legal_students_receive_awards/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="featured-link"
                    >
                      CILEX - Legal Students Receive Awards
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="achievements-box">
              <h3>Achievements</h3>
              <div className="achievement-list">
                <div className="achievement-item">
                  <span className="achievement-medal">🏅</span>
                  <p>
                    Awarded the <strong>City & Guilds Medal for Excellence</strong> — a national
                    recognition granted to only 120 students across the UK for
                    outstanding academic achievement.
                  </p>
                </div>
                <div className="achievement-item">
                  <span className="achievement-medal">🏅</span>
                  <p>
                    Named <strong>City College Coventry's Outstanding Accountancy
                    Student</strong> for 2007–08.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications Section */}
      <section className="section qualifications-section">
        <div className="container">
          <div className="text-center mb-4">
            <h2>Professional Qualifications & Expertise</h2>
            <p className="section-subtitle">
              Certified expertise you can trust for all your accounting needs
            </p>
          </div>
          <div className="grid-2">
            {qualifications.map((qual, index) => (
              <div key={index} className="qualification-card">
                <div className="qual-icon">{qual.icon}</div>
                <h3>{qual.title}</h3>
                <p>{qual.description}</p>
              </div>
            ))}
          </div>
          <div className="expertise-text">
            <p>
              With dual qualifications from ACCA and AAT, our practice upholds
              the highest standards of professional ethics, technical
              competence, and continuing professional development. This ensures
              you receive advice that is current, compliant, and aligned with
              the latest UK tax and accounting regulations.
            </p>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="section experience-section">
        <div className="container">
          <div className="experience-content">
            <h2>15+ Years of UK Practice Experience</h2>
            <p className="lead-text">
              Over more than a decade of practice, we have developed deep
              expertise across diverse industries and client types:
            </p>
            <div className="experience-grid">
              <div className="experience-item">
                <CheckCircle size={22} />
                <div>
                  <strong>Limited Companies:</strong> From micro-entities to
                  growing SMEs across retail, construction, hospitality, and
                  professional services
                </div>
              </div>
              <div className="experience-item">
                <CheckCircle size={22} />
                <div>
                  <strong>Sole Traders & Partnerships:</strong> Freelancers,
                  consultants, tradespeople, and small business owners
                </div>
              </div>
              <div className="experience-item">
                <CheckCircle size={22} />
                <div>
                  <strong>Landlords:</strong> Property rental income, capital
                  gains, and portfolio tax planning
                </div>
              </div>
              <div className="experience-item">
                <CheckCircle size={22} />
                <div>
                  <strong>Start-Ups:</strong> New ventures requiring structure
                  advice, formation, and first-year support
                </div>
              </div>
              <div className="experience-item">
                <CheckCircle size={22} />
                <div>
                  <strong>HMRC Cases:</strong> Enquiries, investigations,
                  disputes, and compliance matters
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section section-alt values-section">
        <div className="container">
          <div className="text-center mb-4">
            <h2>Our Core Values</h2>
            <p className="section-subtitle">
              The principles that guide everything we do for our clients
            </p>
          </div>
          <div className="grid-2">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <div>
                  <h4>{value.title}</h4>
                  <p>{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Commitment Section */}
      <section className="section local-commitment">
        <div className="container">
          <div className="commitment-content">
            <h2>Committed to Wolverhampton & the West Midlands</h2>
            <p>
              As a locally-based practice, we understand the unique challenges
              and opportunities facing businesses in Wolverhampton, Dudley,
              Walsall, Bilston, and throughout the West Midlands. From
              supporting established family businesses to helping ambitious
              start-ups get off the ground, we're proud to contribute to the
              financial success of our local community.
            </p>
            <p>
              Our knowledge of the regional business environment, combined with
              national-level technical expertise, means we deliver solutions
              that are both practical and effective for businesses operating in
              our area.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section about-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Work With a Qualified Professional</h2>
            <p>
              Experience the difference that professional qualifications, local
              knowledge, and personal service make to your financial management.
            </p>
            <Link
              to="/contact"
              className="btn-primary btn-large"
              data-testid="about-cta-contact"
            >
              <Mail size={22} />
              Contact Our Accountant
            </Link>
            <p className="cta-note">
              Receive a response from our FCCA-qualified accountant within 24
              hours.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
